import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import { createCustomer, getAdminCustomerById, updateCustomer } from 'apis/adminCustomers';
import { getAdminCustomerCategories } from 'apis/adminCustomerCategories';
import {
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const CustomersForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({ customerCategoryId: '', navMenuId: '', name: '', logoImage: '', websiteUrl: '', sortOrder: 0, isActive: true });

    const { data: categoriesData = { items: [] } } = useAdminListQuery('customer-categories-options', { pageSize: 1000 }, getAdminCustomerCategories);
    const categories = categoriesData?.items || categoriesData?.data?.items || categoriesData?.data || [];

    const { data: detail, error: detailError } = useAdminDetailQuery('customers', id, getAdminCustomerById);
    const createMutation = useAdminCreateMutation('customers', createCustomer);
    const updateMutation = useAdminUpdateMutation('customers', updateCustomer);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (detail) setFormData({ ...detail, customerCategoryId: detail.customerCategoryId || detail.CustomerCategoryId || '', navMenuId: detail.navMenuId || '' });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                customerCategoryId: formData.customerCategoryId ? Number(formData.customerCategoryId) : null,
                navMenuId: formData.navMenuId ? Number(formData.navMenuId) : 19,
                sortOrder: Number(formData.sortOrder)
            };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/customers'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Khách hàng' : 'Thêm Khách hàng'} subtitle="Thêm logo khách hàng hoặc đối tác vào trang web" />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Thông tin Khách hàng</p>
                <div className="form-row">
                    <Select
                        label="Loại đối tác / Khách hàng *"
                        name="customerCategoryId"
                        value={formData.customerCategoryId}
                        onChange={handleChange}
                        options={[{ value: '', label: 'Chọn loại đối tác / khách hàng...' }, ...categories.map(c => {
                            return {
                                value: c.id,
                                label: c.translations?.[0]?.name || c.name || `Loại #${c.id}`
                            };
                        })]}
                        required
                        error={errors.customerCategoryId}
                    />
                    <Input label="Tên" name="name" value={formData.name} onChange={handleChange} placeholder="Tên khách hàng" />
                </div>
                <ImageUploadField
                    label="Hình ảnh Logo *"
                    value={formData.logoImage}
                    folder="customers"
                    onChange={(logoImage) => setFormData(prev => ({ ...prev, logoImage }))}
                />
                <Input label="URL Website" name="websiteUrl" value={formData.websiteUrl} onChange={handleChange} placeholder="https://example.com" />
                <div className="form-row">
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật Khách hàng' : 'Thêm Khách hàng'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/customers')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default CustomersForm;
